import {
  Component,
  effect,
  afterRenderEffect,
  ElementRef,
  Injector,
  NgZone,
  Renderer2,
  OnInit,
  AfterViewInit,
  OnDestroy,
  signal,
  computed,
  ChangeDetectorRef,
  inject,
  untracked,
  input,
  output,
  viewChild,
} from '@angular/core';
import { AnimatedIconComponent } from '../animated-icon/animated-icon.component';
import { IndicatorComponent } from '../indicator/indicator.component';
import { DynamicViewDirective, isComponent, isTemplateRef } from '@ngneat/overview';
import { ENTER_ANIMATION_DURATION, EXIT_ANIMATION_DURATION, HOT_TOAST_DEPTH_SCALE } from '../../constants';
import { HotToastRef } from '../../hot-toast-ref';
import { Toast, ToastConfig, CreateHotToastRef, HotToastClose, HotToastGroupEvent } from '../../hot-toast.model';
import { animate } from '../../utils';

@Component({
  selector: 'hot-toast-group-item',
  templateUrl: 'hot-toast-group-item.component.html',

  imports: [AnimatedIconComponent, IndicatorComponent, DynamicViewDirective],
})
export class HotToastGroupItemComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly toast = input.required<Toast<unknown>>();
  readonly offset = input(0);
  readonly defaultConfig = input<ToastConfig>();
  readonly toastRef = input<CreateHotToastRef<unknown>>();
  readonly toastsAfter = input(0);

  readonly isShowingAllToasts = input(false);

  readonly height = output<number>();
  readonly beforeClosed = output();
  readonly afterClosed = output<HotToastClose>();
  readonly showAllToasts = output<boolean>();
  readonly toggleGroup = output<HotToastGroupEvent>();

  protected readonly toastBarBase = viewChild.required<ElementRef<HTMLElement>>('hotToastBarBase');

  isManualClose = false;
  context!: Record<string, unknown>;
  toastComponentInjector!: Injector;
  toastBarBaseStylesSignal = signal({});

  private unlisteners: VoidFunction[] = [];
  private rafIds: number[] = [];
  private destroyed = false;
  protected softClosed = false;

  private injector = inject(Injector);
  private renderer = inject(Renderer2);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  private isFirstToastChange = true;

  constructor() {
    // Replaces the former `toast` setter side effect: recompute the base styles whenever the toast input changes.
    effect(() => {
      const value = this.toast();
      // Read the current style without tracking it, otherwise this effect would re-run on its own writes.
      const ogStyle = untracked(() => this.toastBarBaseStylesSignal());
      const newStyle: Record<string, string> = { ...value.style };

      if ((ogStyle as Record<string, string>)['animation']?.includes('hotToastExitAnimation')) {
        // if toast is set for exit, we don't need want set the enter animation
        (newStyle as Record<string, string>)['animation'] = (ogStyle as Record<string, string>)['animation'];
      } else {
        const top = value.position?.includes('top');
        const enterAnimation = `hotToastEnterAnimation${
          top ? 'Negative' : 'Positive'
        } ${ENTER_ANIMATION_DURATION}ms cubic-bezier(0.21, 1.02, 0.73, 1) forwards`;
        newStyle['animation'] = enterAnimation;
      }

      untracked(() => this.toastBarBaseStylesSignal.set(newStyle));
    });

    // Replaces the former `ngOnChanges` handler. Uses `afterRenderEffect`'s `read` phase since this
    // only reads the DOM (offsetHeight) and must run after Angular commits, not mid-change-detection.
    afterRenderEffect({
      read: () => {
        const value = this.toast();
        if (this.isFirstToastChange) {
          this.isFirstToastChange = false;
          return;
        }
        if (value?.message) {
          this.height.emit(this.nativeElement().offsetHeight);
        }
      },
    });
  }

  readonly message = computed(() => this.toast().message);
  readonly nativeElement = computed(() => this.toastBarBase().nativeElement);

  readonly scale = computed(() =>
    this.defaultConfig()?.stacking !== 'vertical' && !this.isShowingAllToasts()
      ? this.toastsAfter() * -HOT_TOAST_DEPTH_SCALE + 1
      : 1,
  );

  readonly translateY = computed(() => this.offset() * (this.top() ? 1 : -1) + 'px');

  readonly exitAnimationDelay = computed(() => this.toast().duration + 'ms');

  readonly top = computed(() => this.toast().position?.includes('top'));

  readonly containerPositionStyle = computed(() => {
    const verticalStyle = this.top() ? { top: 0 } : { bottom: 0 };
    const transform = `translateY(var(--hot-toast-translate-y)) scale(var(--hot-toast-scale))`;

    const horizontalStyle = this.toast().position?.includes('left')
      ? {
          left: 0,
        }
      : this.toast().position?.includes('right')
        ? {
            right: 0,
          }
        : {
            left: 0,
            right: 0,
            justifyContent: 'center',
          };
    return {
      transform,
      ...verticalStyle,
      ...horizontalStyle,
    };
  });

  readonly isIconString = computed(() => typeof this.toast().icon === 'string');

  ngOnInit() {
    if (isTemplateRef(this.message())) {
      this.context = { $implicit: this.toastRef() };
    }
    if (isComponent(this.message())) {
      this.toastComponentInjector = Injector.create({
        providers: [
          {
            provide: HotToastRef,
            useValue: this.toastRef(),
          },
        ],
        parent: this.toast().injector || this.injector,
      });
    }

    const nativeElement = this.nativeElement();
    // Caretaker note: `animationstart` and `animationend` events are event tasks that trigger change detection.
    // We'd want to trigger the change detection only if it's an exit animation.
    this.ngZone.runOutsideAngular(() => {
      this.unlisteners.push(
        // Caretaker note: we have to remove these event listeners at the end (even if the element is removed from DOM).
        this.renderer.listen(nativeElement, 'animationstart', (event: AnimationEvent) => {
          if (this.isExitAnimation(event)) {
            this.ngZone.run(() => {
              this.renderer.setStyle(nativeElement, 'pointer-events', 'none');
              this.renderer.setStyle(nativeElement.parentElement, 'pointer-events', 'none');
              this.beforeClosed.emit();
            });
          }
        }),
        this.renderer.listen(nativeElement, 'animationend', (event: AnimationEvent) => {
          if (this.isEnterAnimation(event)) {
            this.ngZone.run(() => {
              if (this.toast().autoClose) {
                const exitAnimation = `hotToastExitAnimation${
                  this.top() ? 'Negative' : 'Positive'
                } ${EXIT_ANIMATION_DURATION}ms forwards cubic-bezier(0.06, 0.71, 0.55, 1) var(--hot-toast-exit-animation-delay) var(--hot-toast-exit-animation-state)`;
                this.toastBarBaseStylesSignal.set({ ...this.toast().style, animation: exitAnimation });
              }
            });
          }
          if (this.isExitAnimation(event)) {
            this.ngZone.run(() =>
              this.afterClosed.emit({ dismissedByAction: this.isManualClose, id: this.toast().id }),
            );
          }
        }),
      );
    });
  }

  ngAfterViewInit() {
    const nativeElement = this.nativeElement();
    // Caretaker note: accessing `offsetHeight` triggers the whole layout update.
    // Macro tasks (like `setTimeout`) might be executed within the current rendering frame and cause a frame drop.
    const rafId = requestAnimationFrame(() => {
      this.rafIds = this.rafIds.filter((id) => id !== rafId);
      if (this.destroyed) return;
      this.height.emit(nativeElement.offsetHeight);
    });
    this.rafIds.push(rafId);

    this.setToastAttributes();
  }

  softClose() {
    const exitAnimation = `hotToastExitSoftAnimation${
      this.top() ? 'Negative' : 'Positive'
    } ${EXIT_ANIMATION_DURATION}ms forwards cubic-bezier(0.06, 0.71, 0.55, 1)`;

    const nativeElement = this.nativeElement();

    animate(this.renderer, nativeElement, exitAnimation);
    this.softClosed = true;
  }
  softOpen() {
    const softEnterAnimation = `hotToastEnterSoftAnimation${
      this.top() ? 'Negative' : 'Positive'
    } ${ENTER_ANIMATION_DURATION}ms cubic-bezier(0.21, 1.02, 0.73, 1) forwards`;

    const nativeElement = this.nativeElement();

    animate(this.renderer, nativeElement, softEnterAnimation);
    this.softClosed = false;
  }

  close() {
    this.isManualClose = true;
    this.cdr.markForCheck();

    const exitAnimation = `hotToastExitAnimation${
      this.top() ? 'Negative' : 'Positive'
    } ${EXIT_ANIMATION_DURATION}ms forwards cubic-bezier(0.06, 0.71, 0.55, 1)`;
    this.toastBarBaseStylesSignal.set({ ...this.toast().style, animation: exitAnimation });
  }

  handleMouseEnter() {
    this.showAllToasts.emit(true);
  }
  handleMouseLeave() {
    this.showAllToasts.emit(false);
  }

  ngOnDestroy() {
    this.destroyed = true;
    while (this.rafIds.length) {
      cancelAnimationFrame(this.rafIds.pop() || 0);
    }
    this.close();
    while (this.unlisteners.length) {
      this.unlisteners.pop()?.();
    }
  }

  private isExitAnimation(ev: AnimationEvent) {
    return ev.animationName.includes('hotToastExitAnimation');
  }

  private isEnterAnimation(ev: AnimationEvent) {
    return ev.animationName.includes('hotToastEnterAnimation');
  }

  private setToastAttributes() {
    const toastAttributes: Record<string, string> = this.toast().attributes || {};
    for (const [key, value] of Object.entries(toastAttributes)) {
      this.renderer.setAttribute(this.nativeElement(), key, value);
    }
  }
}
